import { Restaurant, SeatingData, SeatingEmailType } from '@maverick/entity';
import { Holder, Icon, IconMapPin, Loading } from '@maverick/ui';
import { FC, useState, useEffect } from 'react';
import { CmsInputModes, InputCms } from '@maverick/cms';
import { useRouter } from 'next/router';
import { SeatingManager } from 'src/manager/Seating.manager';
import { SeatingModal } from './SeatingModal';
import { format, differenceInMilliseconds } from 'date-fns';
import { LocationManager } from 'src/manager/Location.manager';
import { useInput } from '@maverick/hooks';
import * as Styled from './Seating.styled';
import { GaEvent } from '@maverick/utils';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { SeatingReduxProps } from '.';

interface SeatingDetailsProps extends SeatingReduxProps {}
export const SeatingDetails: FC<SeatingDetailsProps> = ({ seatingData, setSeatingData, setSeatingDataModify }) => {
	const router = useRouter();
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<string>('');
	const [restaurant, setRestaurant] = useState<Restaurant>();
	const [confirmCancel, setConfirmCancel] = useState<boolean>(false);

	const [share, setShare] = useState<boolean>(false);
	const shareEmails = useInput('', { label: "Friend's email(s)" });
	const shareMessage = useInput('', { label: 'Message' });
	const [shareStatus, setShareStatus] = useState<{ title: string; text?: string } | null>(null);

	const { sitecoreContext } = useSitecoreContext();

	useEffect(() => {
		init();
	}, [router]);

	const init = () => {
		if (router.isReady) {
			if (!!router.query.partyid) {
				initFromQueryParam();
				return;
			}

			initFromLocal();
		}
	};

	const initFromQueryParam = async () => {
		const { unit, partyid } = router.query;

		setLoading(true);
		const { error, seatingData } = await SeatingManager.GetReservation(unit as string, partyid as string);
		setLoading(false);

		if (!!error) {
			const errorMessage = error ?? 'Unexpected error. Please try again later.';

			setError(errorMessage);
			GaEvent.ErrorView(errorMessage, sitecoreContext.route?.displayName);

			return;
		}

		if (!seatingData) {
			setError('Unexpected error. Please try again later.');

			GaEvent.ErrorView('Unexpected error. Please try again later.', sitecoreContext.route?.displayName);

			return;
		}

		setSeatingData(seatingData);
		getAddress(seatingData);
	};

	const initFromLocal = async () => {
		if (!seatingData?.PartyKey) {
			router.push('/seating');
			return;
		}

		getAddress(seatingData);
		setLoading(false);
	};

	const getAddress = async (seatingData: SeatingData) => {
		const inThePast = differenceInMilliseconds(seatingData.Datetime, new Date()) < 0;

		if (inThePast) {
			setSeatingData(null);
			setError('Expired');

			GaEvent.ErrorView('Expired', sitecoreContext.route?.displayName);

			return;
		}

		setLoading(true);
		const result = await LocationManager.GetLocationByRef(seatingData.LocationId);
		setLoading(false);

		if (!result) return;

		setRestaurant(result);
		const _seating = new SeatingData();
		Object.assign(_seating, seatingData);
		_seating.LocationName = result.name;
		setSeatingData(_seating);
	};

	const formatTimeSlot = (): string => {
		if (!seatingData?.Datetime) return '';

		return format(seatingData.Datetime, 'h:mm aaa');
	};

	const cancelReservation = async () => {
		if (!seatingData) return;

		setConfirmCancel(false);
		setLoading(true);
		const canceled = await SeatingManager.CancelReservation(seatingData);
		setLoading(false);

		if (canceled) {
			setSeatingData(null);
			router.push('/seating');
		}
	};

	const submitShare = async () => {
		if (!shareEmails.value) {
			shareEmails.setError('Please enter one or more valid emails');

			GaEvent.ErrorView('Please enter one or more valid emails', sitecoreContext.route?.displayName);

			return;
		}

		const seating = new SeatingData({ ...seatingData });
		seating.ShareEmails = shareEmails.value;
		seating.ShareMessage = shareMessage.value;
		setShare(false);
		setLoading(true);
		const success = await SeatingManager.SendReservationEmail(seating, SeatingEmailType.ShareConfirmation);
		setLoading(false);

		if (success) {
			setShareStatus({ title: 'Email is on its way!' });
		} else {
			GaEvent.ErrorView('Message could not be sent please try again.', sitecoreContext.route?.displayName);
			setShareStatus({ title: 'Error', text: 'Message could not be sent please try again.' });
		}
	};

	const handleModify = () => {
		setSeatingDataModify(seatingData);
		router.push('/seating');
	};

	return (
		<>
			<Styled.Container>
				{loading && (
					<Styled.Loading>
						<Loading />
					</Styled.Loading>
				)}

				<Holder>
					{seatingData && (
						<>
							<h1>
								You're on the list. {seatingData.Guests} guests, plan to arrive today by{' '}
								{formatTimeSlot()}
							</h1>
							<Styled.Location>
								<div>
									<Icon icon={IconMapPin} customSize />
								</div>
								<span>{seatingData.LocationName}</span>
							</Styled.Location>

							{restaurant && (
								<Styled.Inline>
									<Styled.Item>
										<strong>Address</strong>
										<p>
											<a href={Restaurant.AddressLink(restaurant)}>
												{restaurant.streetaddress}
												<br />
												{restaurant.city}, {restaurant.state} {restaurant.zip}
											</a>
											<br />
											{restaurant.telephone}
										</p>
									</Styled.Item>

									<Styled.Item>
										<strong>Send to friends</strong>
										<p>
											<a onClick={() => setShare(true)}>Share the confirmation details</a>
										</p>
									</Styled.Item>
								</Styled.Inline>
							)}

							<Styled.DetailsButtons>
								<Styled.Button onClick={handleModify}>Modify</Styled.Button>
								<p onClick={() => setConfirmCancel(true)}>Remove my name from the list</p>
							</Styled.DetailsButtons>
						</>
					)}
				</Holder>
			</Styled.Container>

			{!!error && (
				<SeatingModal>
					<h2>Error</h2>
					<p>{error}</p>
					<Styled.Button onClick={() => router.push('/')}>Ok</Styled.Button>
				</SeatingModal>
			)}

			{confirmCancel && (
				<SeatingModal onClose={() => setConfirmCancel(false)}>
					<h2>Are you sure?</h2>
					<p>This will remove you from the list.</p>
					<Styled.Button onClick={cancelReservation}>Yes, I'm sure</Styled.Button>
				</SeatingModal>
			)}

			{share && (
				<SeatingModal onClose={() => setShare(false)}>
					<h2>Send to friends</h2>
					<Styled.ShareFields>
						<InputCms handler={shareEmails} variant='terciary' mode={CmsInputModes.Email} />
						<InputCms handler={shareMessage} variant='terciary' mode={CmsInputModes.None} />
					</Styled.ShareFields>
					<Styled.Button onClick={submitShare}>Share</Styled.Button>
				</SeatingModal>
			)}

			{!!shareStatus && (
				<SeatingModal onClose={() => setShareStatus(null)}>
					<h2>{shareStatus.title}</h2>
					{!!shareStatus.text && <p>{shareStatus.text}</p>}
					<Styled.Button onClick={() => setShareStatus(null)}>Ok</Styled.Button>
				</SeatingModal>
			)}
		</>
	);
};
